import { useState, useCallback, useEffect, useRef } from 'react'
import type { FormData, Currency, BillingCycle, PartialFormData } from '@/types/apply'

const STORAGE_KEY = 'wavbee-apply-form'
const OLD_STORAGE_KEY = 'applicationFormData'

function emptyFormData(defaultCurrency: Currency): FormData {
  return {
    personal_info: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      phone_dial_code: '',
      phone_iso_code: '',
      website: ''
    },
    address: {
      street_address: '',
      address_line_2: '',
      city: '',
      state: '',
      postal_code: '',
      country: ''
    },
    role_info: {
      role: '',
      artist_name: '',
      label_name: '',
      artists_under_management: ''
    },
    demo_links: [''],
    social_media: {
      instagram: '',
      tiktok: '',
      twitter: '',
      facebook: ''
    },
    subscription: {
      plan: '',
      billing_cycle: 'yearly' as BillingCycle,
      currency: defaultCurrency,
      invitation_code: ''
    },
    agreements: {
      terms_of_service: false,
      ai_music_policy:  false,
      reapply_policy:   false
    }
  }
}

function loadFromStorage(defaultCurrency: Currency): { formData: FormData; currentStep: number } {
  try {
    // Migrate from old key if present
    const oldData = window.localStorage.getItem(OLD_STORAGE_KEY)
    if (oldData) {
      window.localStorage.removeItem(OLD_STORAGE_KEY)
    }

    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return {
        formData: { ...emptyFormData(defaultCurrency), ...parsed.formData },
        currentStep: parsed.currentStep || 1
      }
    }
  } catch {
    // Corrupted data — start fresh
  }
  return { formData: emptyFormData(defaultCurrency), currentStep: 1 }
}

export function useFormState(defaultCurrency: Currency) {
  const initialized = useRef(false)
  const [formData, setFormData] = useState<FormData>(() => emptyFormData(defaultCurrency))
  const [currentStep, setCurrentStep] = useState(1)

  // Load from localStorage on mount
  useEffect(() => {
    if (initialized.current) return
    initialized.current = true
    const saved = loadFromStorage(defaultCurrency)
    setFormData(saved.formData)
    setCurrentStep(saved.currentStep)
  }, [defaultCurrency])

  // Persist on every change
  useEffect(() => {
    if (!initialized.current) return
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ formData, currentStep }))
    } catch {
      // Storage full — ignore
    }
  }, [formData, currentStep])

  const updateField = useCallback(<S extends keyof FormData>(
    step: S,
    field: keyof FormData[S],
    value: FormData[S][keyof FormData[S]]
  ) => {
    setFormData(prev => ({
      ...prev,
      [step]: { ...prev[step], [field]: value }
    }))
  }, [])

  const updateDemoLinks = useCallback((links: string[]) => {
    setFormData(prev => ({ ...prev, demo_links: links }))
  }, [])

  // Merge a group-aware patch in one update — used by the WebMCP prefill tool
  // to set many fields at once without a re-render per field. Each group is
  // shallow-merged so untouched fields survive; demo_links is replaced whole.
  const mergeFormData = useCallback((patch: PartialFormData) => {
    setFormData(prev => ({
      personal_info: { ...prev.personal_info, ...patch.personal_info },
      address:       { ...prev.address,       ...patch.address },
      role_info:     { ...prev.role_info,     ...patch.role_info },
      demo_links:    patch.demo_links ?? prev.demo_links,
      social_media:  { ...prev.social_media,  ...patch.social_media },
      subscription:  { ...prev.subscription,  ...patch.subscription },
      agreements:    { ...prev.agreements,    ...patch.agreements }
    }))
  }, [])

  const nextStep = useCallback(() => {
    setCurrentStep(prev => Math.min(prev + 1, 6))
  }, [])

  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 1))
  }, [])

  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= 6) setCurrentStep(step)
  }, [])

  const resetForm = useCallback(() => {
    setFormData(emptyFormData(defaultCurrency))
    setCurrentStep(1)
    try {
      window.localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Ignore
    }
  }, [defaultCurrency])

  return {
    formData,
    currentStep,
    updateField,
    updateDemoLinks,
    mergeFormData,
    nextStep,
    prevStep,
    goToStep,
    resetForm
  }
}
